import React, { useState } from "react";

const API_URL = window.location.origin;

// A friendly set of avatar icons the user can pick from (self-contained, no
// external images). The chosen emoji is stored on the user record.
const AVATARS = [
  "\uD83D\uDE00", "\uD83D\uDE0E", "\uD83E\uDD13", "\uD83D\uDE07", "\uD83E\uDD20",
  "\uD83E\uDDD1\u200D\uD83D\uDCBB", "\uD83D\uDC69\u200D\uD83D\uDCBB", "\uD83D\uDC68\u200D\uD83D\uDCBC", "\uD83D\uDC69\u200D\uD83D\uDCBC", "\uD83E\uDDD1\u200D\uD83C\uDFA8",
  "\uD83D\uDE38", "\uD83D\uDC36", "\uD83E\uDD8A", "\uD83D\uDC3C", "\uD83D\uDC27",
  "\uD83E\uDD84", "\uD83D\uDC32", "\uD83E\uDD81", "\uD83D\uDC2F", "\uD83D\uDC38",
  "\uD83D\uDE80", "\u26A1", "\uD83C\uDF1F", "\uD83C\uDF08"
];

export default function ProfilePage({ currentUser, onUpdated }) {
  const [name, setName] = useState(currentUser?.name || "");
  const [avatar, setAvatar] = useState(currentUser?.avatar || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");

  const initial = String(name || currentUser?.email || "U").charAt(0).toUpperCase();

  async function saveChanges() {
    setErr("");
    setMsg("");
    if (!name.trim()) {
      setErr("Please enter your name.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), avatar })
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data.message || "Could not save your profile.");
        return;
      }
      setMsg("Your changes have been saved.");
      if (onUpdated && data.user) onUpdated(data.user);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function savePassword() {
    setPwErr("");
    setPwMsg("");
    if (!currentPassword) {
      setPwErr("Please enter your current password.");
      return;
    }
    if (newPassword.length < 6) {
      setPwErr("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwErr("New password and confirmation do not match.");
      return;
    }
    setPwSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await res.json();
      if (!res.ok) {
        setPwErr(data.message || "Could not update your password.");
        return;
      }
      setPwMsg("Your password has been updated.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPw(false);
    } catch (e) {
      setPwErr(e.message);
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <section className="page-card sx-profile-page">
      <h2>Profile</h2>

      <div className="sx-profile-current">
        <div className="sx-profile-current-avatar">{avatar || initial}</div>
      </div>

      <div className="sx-profile-avatar-grid">
        {AVATARS.map((a) => (
          <button
            type="button"
            key={a}
            className={avatar === a ? "sx-profile-avatar sel" : "sx-profile-avatar"}
            onClick={() => setAvatar(a)}
            aria-label="Choose avatar"
          >
            {a}
          </button>
        ))}
      </div>

      <div className="sx-profile-form">
        <label className="sx-profile-label">Name</label>
        <input
          className="sx-profile-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
        />

        <label className="sx-profile-label">Email address</label>
        <input
          className="sx-profile-input"
          value={currentUser?.email || ""}
          readOnly
          disabled
        />
        <p className="sx-profile-hint">Email address cannot be changed</p>

        <label className="sx-profile-label">Password</label>
        <div className="sx-profile-pw-row">
          <input
            className="sx-profile-input"
            type="password"
            value="************"
            readOnly
            disabled
          />
          <button
            type="button"
            className="sx-profile-reset-btn"
            onClick={() => {
              setShowPw((v) => !v);
              setPwErr("");
              setPwMsg("");
            }}
          >
            Reset Password
          </button>
        </div>

        {showPw ? (
          <div className="sx-profile-pw-panel">
            <label className="sx-profile-label">Current password</label>
            <input
              className="sx-profile-input"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
            <label className="sx-profile-label">New password</label>
            <input
              className="sx-profile-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
            />
            <label className="sx-profile-label">Confirm new password</label>
            <input
              className="sx-profile-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
            />
            {pwErr ? <div className="sx-profile-error">{pwErr}</div> : null}
            <div className="sx-profile-pw-actions">
              <button
                type="button"
                className="sx-profile-ghost"
                onClick={() => setShowPw(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sx-yellow-btn"
                onClick={savePassword}
                disabled={pwSaving}
              >
                {pwSaving ? "Updating\u2026" : "Update Password"}
              </button>
            </div>
          </div>
        ) : null}

        {pwMsg ? <div className="sx-profile-success">{pwMsg}</div> : null}
        {err ? <div className="sx-profile-error">{err}</div> : null}
        {msg ? <div className="sx-profile-success">{msg}</div> : null}

        <div className="sx-profile-save-row">
          <button
            type="button"
            className="sx-yellow-btn"
            onClick={saveChanges}
            disabled={saving}
          >
            {saving ? "Saving\u2026" : "Save changes"}
          </button>
        </div>
      </div>
    </section>
  );
}
